import React from 'react';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { HomePageCarouselModelType } from 'src/api/config/home_page_carousel/getHomePageCarousel';
import { ParamsType } from 'src/api_openapi/generated/openapi_website_cms_createHomePageCarousel';
import { UploadImageState } from 'src/AppState/UploadImageState/UploadImageState';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { HomePageCarouselSelectionState } from './HomePageCarouselSelectionState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { arrayMove } from 'react-movable';
import { assertNever } from 'src_common/common/assertNever';
import { TypeId } from 'src_common/common/websocket2/type';
import { CampaignsListType, SdkStaff, SportsListType } from 'src/AppState/sdk/staff';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import {
    validateHexColor,
    validateExpectLink,
    convertAndValidToFloat,
    validateCommaSeparatedFloats,
} from 'src_common/common/mobx-utils/Form2/validators';
import { CmsTranslationsState } from 'src/AppState/CmsState/CmsTranslationsState/CmsTranslationsState';
import {
    PromoCarouselTranslationFormType,
    PromoCarouselTranslationsState,
} from 'src/main/cms/HomePageCarousel/HomePageCarouselForm/PromoCarouselTranslationsFormState';
import { CmsTranslationDataModelType } from 'src/api/config/cmsTranslations/getAllCmsTranslations';
import { LanguagesState } from 'src/AppState/CmsState/LanguagesState/LanguagesState';
import { promoCarouselComponentType } from 'src/main/cms/HomePageCarousel/CarouselMainState.state';
import { OptionType } from 'src/AppState/ConfigState/types';
import { CmsDisplayOrderState } from 'src/AppState/CmsState/CmsDisplayOrderState/CmsDisplayOrderState';
import dayjs from 'dayjs';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { HomePageCarouselBettingForm } from 'src/main/cms/HomePageCarousel/HomePageCarouselForm/HomePageCarouselBettingForm';
import { HomePageCarouselPromoForm } from 'src/main/cms/HomePageCarousel/HomePageCarouselForm/HomePageCarouselPromoForm';
import { HomePageCarouselBonusEnginePromoForm } from 'src/main/cms/HomePageCarousel/HomePageCarouselForm/HomePageCarouselBonusEnginePromoForm';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import { Common } from 'src/AppState/Common';
import {
    isDateAfter,
    isDateBefore,
    validateIf,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';
import { apiDateToFormDate } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import { ToasterState } from 'src/AppState/ToasterState';
import { validateLength } from 'src/api/utils/form2Validators';

const TranslationIO = t.interface({
    title: t.string,
    subtitle: t.string,
    btn_title: t.string,
    tc_text: t.string,
});

const isTranslation = createGuard(TranslationIO);

const SelectionsDetailsIO = t.interface({
    id: t.number,
    label: t.string,
});

export type TranslationFormType = {
    btn_title: null | undefined | string;
    subtitle: null | undefined | string;
    tc_text: null | undefined | string;
    title: null | undefined | string;
};

type SelectionsDetailsType = t.TypeOf<typeof SelectionsDetailsIO>;

const isSelectionsDetails = createGuard(t.array(SelectionsDetailsIO));

type HomePageCarouselBodyType = ParamsType['body'];

export interface HomePageCarouselType {
    action_template: string;
    campaign_id?: string;
    bg_color: string;
    btn_color: string;
    btn_link: string;
    btn_title: string;
    competition_id: number | null;
    date_from: string;
    date_to: string;
    event_id: number | null;
    game_id: number | null;
    img_url: string;
    is_active: boolean;
    market_id: number | null;
    subtitle: string;
    tc_text: string;
    title: string;
    sport: string;
    is_desktop: boolean;
    is_mobile: boolean;
    users_ids?: string | null;
    show_for_all?: boolean | null;
    stake_factor?: string | null;
    stake_factor_range?: string | null;
    visibility_mode?: string | null;
    email_opt_in_filter?: string | null;
    exclude_promo_banned?: boolean | null;
    xtremepush_campaign_url?: string | null;
}

interface HomePageCarouselLoadingType {
    type: 'loading';
}

interface HomePageCarouselReadyType {
    type: 'ready';
    value: Array<HomePageCarouselModelType>;
}

interface HomePageCarouselErrorType {
    type: 'error';
    value: string;
}

type HomePageCarouselContentResponseType =
    | HomePageCarouselLoadingType
    | HomePageCarouselReadyType
    | HomePageCarouselErrorType;

interface OptionTypeOldType {
    key: string;
    name: string;
}

export interface CompetitionSportType {
    competitionId: number | null;
    sport: string;
}

export type CarouselModeType = 'homepage' | 'casino' | 'virtuals' | 'live-casino';

interface HomePageCarouselPromoFormPropsType {
    carouselPromoState: HomePageCarouselState;
    dataTest: string;
}

type HomePageCarouselViewType = (({
    carouselPromoState,
    dataTest,
}: HomePageCarouselPromoFormPropsType) => React.JSX.Element) & {
    displayName: string;
};

export enum PromoLabel {
    Promo = 'Default Promo',
    Betting = 'Betting Promo',
    BonusEngine = 'Bonus Engine Promo',
}

enum HomePageCarouselItemType {
    Promo = 'promo',
    Betting = 'betting',
    BonusEngine = 'bonus-engine',
}

enum PromoBannedCustomerType {
    Exclude = 'exclude',
    Include = 'include',
}

enum EmailOptedType {
    OptedIn = 'optedIn',
    OptedOut = 'optedOut',
    All = 'all',
}

type HomePageCarouselTabType = {
    dataTest: string;
    type: HomePageCarouselItemType;
    label: string;
};

export class HomePageCarouselState {
    @observable public readonly promoCarouselListResource: Resource<Array<HomePageCarouselModelType>>;
    @observable private readonly allSports: Resource<SportsListType>;
    @observable private readonly campaignsResourse: Resource<CampaignsListType>;
    @observable public isEditMode: boolean = false;
    @observable public isDuplicateMode: boolean = false;
    @observable private onModifyHomePageCarousel: HomePageCarouselModelType | null = null;
    @observable public errorMessageForList: string | null = null;
    @observable public errorMessageBetting: string | null = null;
    @observable public homePageCarouselItemType: HomePageCarouselItemType = HomePageCarouselItemType.Promo;
    @observable private eventModelId: number | null = null;
    @observable.struct public selectedSelections: Array<number> = [];
    @observable.struct public selectedSelectionsInner: Array<{ id: number; label: string }> = [];
    @observable public showCreatedPopup: boolean = false;
    @observable public showDeletePopup: boolean = false;
    @observable public showBulkDeleteDialog: boolean = false;
    @observable.struct public selectedItemsForBulkDelete: Array<number> = [];
    @observable public editedPromoId: number | null = null;
    @observable public selectedDevice: 'mobile' | 'desktop' | 'both' = 'both';
    @observable public formsIds: Array<number> = [];
    @observable public onEditTranslations: Array<CmsTranslationDataModelType> = [];
    @observable public allSelectedLanguages: Array<OptionType> = [];
    @observable public deleteFormIds: Array<string> = [];
    @observable public promoCarouselListOrder: Array<number> = [];
    @observable public stakeFactorValue: 'range' | 'list' = 'list';
    @observable public promoBannedCustomerValue: PromoBannedCustomerType = PromoBannedCustomerType.Exclude;
    @observable public hiddenLoggedInValue: 'hide' | 'show' = 'show';
    @observable public emailOptedValue: EmailOptedType = EmailOptedType.OptedIn;

    private readonly homePageCarouselSelectionStateMap: MobxMapAutoNew<number, HomePageCarouselSelectionState>;
    private readonly translationFormStateMap: MobxMapAutoNew<number, PromoCarouselTranslationsState>;
    public readonly actionTemplate: FormInputState<string, string>;
    public readonly campaignId: FormInputState<string, string>;
    public readonly title: FormInputState<string, string>;
    public readonly subtitle: FormInputState<string, string>;
    public readonly idsList: FormInputState<string, string>;
    public readonly bgColor: FormInputState<string, string>;
    public readonly btnTitle: FormInputState<string, string>;
    public readonly btnColor: FormInputState<string, string>;
    public readonly btnLink: FormInputState<string, string>;
    public readonly eventId: FormInputState<string, number | null>;
    public readonly gameId: FormInputState<string, number | null>;
    public readonly competitionId: FormInputState<string, number | null>;
    public readonly sport: FormInputState<string, string>;
    public readonly marketId: FormInputState<string, number | null>;
    public readonly imgUrl: FormInputState<string, string>;
    public readonly tcText: FormInputState<string, string>;
    public readonly dateFrom: FormInputState<string, string>;
    public readonly dateTo: FormInputState<string, string>;
    public readonly isActive: FormInputState<boolean, boolean>;
    public readonly stakeFactor: FormInputState<string, string | null>;
    public readonly stakeFactorRangeMin: FormInputState<string, string | null>;
    public readonly stakeFactorRangeMax: FormInputState<string, string | null>;
    public readonly xtremepushCampaignUrl: FormInputState<string, string | null>;

    public isDesktop: FormInputState<boolean, boolean>;
    public isMobile: FormInputState<boolean, boolean>;
    private readonly form: FormModel<HomePageCarouselType>;
    public uploadIcon: UploadImageState;
    public isCreateProcessing: boolean = false;

    public constructor(
        private readonly carouselType: CarouselModeType,
        private readonly modelsState: ModelsState,
        private readonly sdkStaff: SdkStaff,
        public cmsTranslationsState: CmsTranslationsState,
        public languagesState: LanguagesState,
        public cmsDisplayOrderState: CmsDisplayOrderState,
        private readonly trpcClient: TrpcClient,
        private readonly common: Common
    ) {
        this.translationFormStateMap = new MobxMapAutoNew(
            (_formId: number): PromoCarouselTranslationsState => new PromoCarouselTranslationsState()
        );
        this.promoCarouselListResource = new Resource(async (): Promise<Array<HomePageCarouselModelType>> => {
            const response = await this.trpcClient.client.homePageCarousel.getHomePageCarouselV2.query({
                carousel_type: this.carouselType,
                translation: 'en',
            });

            if (response.status === 200) {
                // NOTE: because selections_details is unknown

                return response.body.map((item) => ({
                    ...item,
                    date_from: apiDateToFormDate(item.date_from),
                    date_to: apiDateToFormDate(item.date_to),
                    selections_details: item.selections_details,
                    hiddenLoggedInValue: item.visibility_mode,
                }));
            }

            return [];
        });
        this.allSports = new Resource(async (): Promise<SportsListType> => {
            return await this.sdkStaff.getAllSports();
        });
        this.campaignsResourse = new Resource(async (): Promise<CampaignsListType> => {
            return await this.sdkStaff.getCampaigns();
        });
        this.homePageCarouselSelectionStateMap = new MobxMapAutoNew(
            (selectionId: number): HomePageCarouselSelectionState =>
                new HomePageCarouselSelectionState(
                    this.modelsState,
                    selectionId,
                    () => this.getMarketModel(),
                    () => this.getSelectedSelectionsLabel(selectionId)
                )
        );
        this.actionTemplate = FormInputState.new('');
        this.campaignId = FormInputState.new('').map((value) => {
            return validateIf(
                () => this.homePageCarouselItemType === HomePageCarouselItemType.BonusEngine,
                () => this.canCampaignBeEmpty()(value)
            );
        });
        this.title = FormInputState.new('');
        this.subtitle = FormInputState.new('');
        this.idsList = FormInputState.new('');
        this.bgColor = FormInputState.new('').map(validateHexColor('Color must be hex (#) value'));
        this.btnTitle = FormInputState.new('');
        this.btnColor = FormInputState.new('').map(validateHexColor('Color must be hex (#) value'));
        this.btnLink = FormInputState.new('')
            .map(this.canDirectLinkBeEmpty())
            .map(validateExpectLink('Link format expected'));
        this.eventId = FormInputState.new('')
            .map(this.canEventBeNull())
            .map(this.validateEventDoesntExist('Event ID incorrect - event does not exist'));
        this.gameId = FormInputState.new('').map(this.canGameBeNull());
        this.competitionId = FormInputState.new('').map(this.canCompetitionBeNull());
        this.sport = FormInputState.new('').map(this.canSportBeEmpty());
        this.marketId = FormInputState.new('').map(this.canMarketBeNull());
        this.imgUrl = FormInputState.new('');
        this.tcText = FormInputState.new('');
        this.dateFrom = FormInputState.new('').map(
            isDateBefore(() => this.dateTo.value, false, 'Start date should be before the end date')
        );
        this.dateTo = FormInputState.new('').map(
            isDateAfter(() => this.dateFrom.value, false, 'End date should be after the start date')
        );
        this.isActive = FormInputState.new<boolean>(false);
        this.isDesktop = FormInputState.new<boolean>(true);
        this.isMobile = FormInputState.new<boolean>(true);
        this.stakeFactor = FormInputState.new('').map(this.canStakeFactorListBeEmpty());
        this.stakeFactorRangeMin = FormInputState.new('').map(this.canStakeFactorBeEmpty());
        this.stakeFactorRangeMax = FormInputState.new('').map(this.canStakeFactorBeEmpty());
        this.xtremepushCampaignUrl = FormInputState.new('').map(this.validateXtremepushCampaignUrl());
        this.form = FormModel.group({
            action_template: this.actionTemplate,
            campaign_id: this.campaignId,
            bg_color: this.bgColor,
            btn_color: this.btnColor,
            btn_link: this.btnLink,
            btn_title: this.btnTitle,
            competition_id: this.competitionId,
            date_from: this.dateFrom,
            date_to: this.dateTo,
            event_id: this.eventId,
            game_id: this.gameId,
            img_url: this.imgUrl,
            is_active: this.isActive,
            market_id: this.marketId,
            subtitle: this.subtitle,
            tc_text: this.tcText,
            title: this.title,
            sport: this.sport,
            is_desktop: this.isDesktop,
            is_mobile: this.isMobile,
            users_ids: this.idsList,
            stake_factor: this.stakeFactor,
            stake_factor_range_min: this.stakeFactorRangeMin,
            stake_factor_range_max: this.stakeFactorRangeMax,
            xtremepush_campaign_url: this.xtremepushCampaignUrl,
        });
        this.uploadIcon = new UploadImageState('home_page_carousel/image');
        makeAutoObservable(this);
    }

    @action public refreshCampaigns = async (): Promise<void> => {
        await this.campaignsResourse.refresh();
    };

    @action public handleShowDeletePopup = (): void => {
        this.showDeletePopup = true;
    };

    @action public handleHideDeletePopup = (): void => {
        this.showDeletePopup = false;
    };

    @action public setSelectedLanguages = (newSelectedLanguages: Array<OptionType>): void => {
        this.allSelectedLanguages = newSelectedLanguages;
    };

    @computed public get idsListParsed(): string {
        const idsList = this.idsList.value.split(',').reduce<number[]>((idsList, curr) => {
            const newId = parseInt(curr);
            if (!isNaN(newId)) {
                idsList.push(newId);
            }
            return idsList;
        }, []);
        return Array.from(new Set(idsList)).join(', ');
    }

    @computed public get isDefaultPromo(): boolean {
        if (this.isHomePage) {
            return this.homePageCarouselItemType === HomePageCarouselItemType.Promo;
        }

        return true;
    }

    @computed public get isHomePage(): boolean {
        return this.carouselType === 'homepage';
    }

    @computed public get isCasinoPage(): boolean {
        return this.carouselType === 'casino';
    }

    @action public selectFilter = (filter: HomePageCarouselItemType): void => {
        this.homePageCarouselItemType = filter;
        this.form.reset();
        this.resetTranslationArrays();
    };

    @computed public get pageName(): string {
        switch (this.carouselType) {
            case 'homepage':
                return 'Homepage';
            case 'casino':
                return 'Casino page';
            case 'virtuals':
                return 'Virtuals page';
            case 'live-casino':
                return 'Live Casino page';
            default:
                return assertNever('carouselType', this.carouselType);
        }
    }

    @computed public get HomePageCarouselView(): HomePageCarouselViewType | null {
        switch (this.homePageCarouselItemType) {
            case HomePageCarouselItemType.Promo:
                return HomePageCarouselPromoForm;
            case HomePageCarouselItemType.Betting:
                return HomePageCarouselBettingForm;
            case HomePageCarouselItemType.BonusEngine:
                return HomePageCarouselBonusEnginePromoForm;
            default:
                return null;
        }
    }

    @computed public get isLoading(): boolean {
        return this.promoCarouselListResponse.type === 'loading';
    }

    @computed private get currentCampaign(): CampaignModel | undefined {
        return this.campaignsListData.list.find((campaign) => campaign.id === this.campaignId.value);
    }

    @computed public get optInOnly(): CampaignModel['optInOnly'] {
        return this.currentCampaign?.optInOnly;
    }

    @computed public get campaignIdState(): FormInputState<string, string> {
        if (typeof this.currentCampaign === 'undefined' && this.campaignsListLoading === false) {
            this.campaignId.setValue('');
        }

        return this.campaignId;
    }

    @action public resetForm = (): void => {
        this.form.reset();
        this.isEditMode = false;
        this.selectedDevice = 'both';
        this.editedPromoId = null;
        this.onModifyHomePageCarousel = null;
        this.eventModelId = null;
        this.uploadIcon.handleResetImage();
        this.homePageCarouselItemType = HomePageCarouselItemType.Promo;
        this.isDuplicateMode = false;
        this.resetAllTranslationForms();
    };

    private resetTranslationArrays = (): void => {
        this.allSelectedLanguages = [];
        this.formsIds = [];
    };

    public resetAllTranslationForms = (): void => {
        for (const id of this.formsIds) {
            this.getTranslationFormStateById(id).resetForm();
        }
        this.resetTranslationArrays();
    };

    @action public handleReset = async (): Promise<void> => {
        if (this.isEditMode && this.onModifyHomePageCarousel !== null) {
            await this.setCarousel(this.onModifyHomePageCarousel);
        } else {
            this.resetForm();
        }
    };

    public addLanguageForm = (_e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, id?: number): void => {
        if (id === undefined) {
            const time = new Date().getTime();
            this.formsIds.push(time);
        } else {
            this.formsIds.push(id);
        }
    };

    public getTranslationFormStateById = (formId: number): PromoCarouselTranslationsState => {
        return this.translationFormStateMap.get(formId);
    };

    @action private handleCreateApiCall = async (promoCarousel: HomePageCarouselBodyType): Promise<number | null> => {
        try {
            const response = await this.trpcClient.client.homePageCarousel.postCreateHomePageCarousel.mutate({
                data: promoCarousel,
            });
            if (response.status === 200) {
                return response.body;
            }
            return null;
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action public handleHideCreatedPopup = (): void => {
        this.showCreatedPopup = false;
    };

    @action public handleCreate = async (): Promise<void> => {
        this.form.setAsVisited();
        const { result } = this.form;

        if (this.isBettingSelectionValid === false) {
            await this.handleBettingError('At least one selection needed');
            return;
        }

        if (this.validateTranslation() === false) {
            return;
        }

        if (result.value.type === 'ok') {
            this.isCreateProcessing = true;
            await this.promoCarouselListResource.refresh();

            const topElem = this.promoCarouselSorted[0] ?? null;
            const display_order = topElem === null ? 1 : topElem.display_order + 1;

            const data = {
                ...result.value.data,
                is_active: false,
                img_url: this.uploadIcon.imgUrl,
                display_order: display_order,
                selections_details: this.selectionsDetailsList,
                media_type: this.homePageCarouselItemType,
                carousel_type: this.carouselType,
                users_ids: this.idsListParsed,
                stake_factor: this.stakeFactorValue === 'list' ? this.stakeFactor.value.split(',') : undefined,
                stake_factor_range:
                    this.stakeFactorValue === 'range'
                        ? JSON.stringify({ min: this.stakeFactorRangeMin.value, max: this.stakeFactorRangeMax.value })
                        : undefined,
                email_opt_in_filter: this.emailOptedValue,
                exclude_promo_banned: this.promoBannedCustomerValue === PromoBannedCustomerType.Exclude ? true : false,
                visibility_mode: this.hiddenLoggedInValue,
            };

            const submitResult = await this.handleCreateApiCall(data);
            if (submitResult === null) {
                console.log('Error');
            } else {
                await this.addDisplayOrder(submitResult);
                await this.promoCarouselListResource.refresh();
                await this.addTranslations(submitResult);
                this.showCreatedPopup = true;
                this.resetForm();
            }
            this.isCreateProcessing = false;
        }
    };

    @computed public get componentIdsList(): Array<number> {
        return this.promoCarouselSorted.map((item) => item.id);
    }

    private addDisplayOrder = async (componentId: number): Promise<void> => {
        await this.trpcClient.client.cmsDisplayOrder.changeCmsDisplayOrder.mutate({
            component_type: `${promoCarouselComponentType}-${this.carouselType}`,
            data: {
                order: [componentId, ...this.componentIdsList],
            },
        });
        await this.cmsDisplayOrderState.displayOrderResource.refresh();
    };

    private addTranslations = async (componentId: number): Promise<void> => {
        for (const formId of this.formsIds) {
            const isNewForm = this.getTranslationFormStateById(formId).isNewForm;
            const isDuplicatedForm = this.getTranslationFormStateById(formId).isDuplicatedForm;
            if (isNewForm === true || isDuplicatedForm === true) {
                const form = this.translationFormStateMap.get(formId);
                const formValue = form.getFormValue();
                if (formValue !== null) {
                    await this.handleCreateTranslation(componentId, formValue);
                }
            }
        }
    };

    @action public handleCreateTranslation = async (
        componentId: number,
        formValue: PromoCarouselTranslationFormType
    ): Promise<void> => {
        const data = {
            component_id: componentId,
            component_type: promoCarouselComponentType,
            language: formValue.language,
            translation: {
                title: formValue.title,
                subtitle: formValue.subtitle,
                btn_title: formValue.btn_title,
                tc_text: formValue.tc_text,
            },
        };

        const submitResult = await this.cmsTranslationsState.handleCreateApiCall(data);
        if (submitResult === null) {
            console.log('Error creating translation');
            await this.handleError('Something went wrong creating translation.');
        } else {
            await this.cmsTranslationsState.translationsResource.refresh();
        }
    };

    private checkPromoCarouselStatus = (startDate: string, endDate: string): string => {
        const currentDate = dayjs();

        if (currentDate.isBefore(dayjs(startDate))) {
            return 'scheduled';
        }

        if (currentDate.isAfter(dayjs(endDate))) {
            return 'ended';
        }

        return 'live';
    };

    private handleChangeStatus = async (componentId: number): Promise<void> => {
        const editedItem = this.onModifyHomePageCarousel;
        const updatedItem = this.promoCarouselList.find((item) => item.id === componentId);

        if (editedItem !== null && updatedItem !== undefined) {
            const statusOnEdit = this.checkPromoCarouselStatus(updatedItem.date_from, updatedItem.date_to);
            const statusOnUpdate = this.checkPromoCarouselStatus(editedItem.date_from, editedItem.date_to);

            if (statusOnUpdate !== statusOnEdit) {
                if (statusOnEdit === 'ended' || statusOnUpdate === 'ended') {
                    const updatedItemData = {
                        ...updatedItem,
                        is_active: false,
                        exclude_promo_banned:
                            this.promoBannedCustomerValue === PromoBannedCustomerType.Exclude ? true : false,
                        hidden_logged_in: this.hiddenLoggedInValue,
                        visibility_mode: undefined,
                    };

                    try {
                        await this.trpcClient.client.homePageCarousel.putUpdateHomePageCarousel.mutate({
                            component_id: componentId,
                            data: updatedItemData,
                        });
                    } catch (error) {
                        console.error(error);
                    }

                    const editItemIndex = this.componentIdsList.indexOf(componentId);
                    this.componentIdsList.splice(editItemIndex, 1);

                    await this.trpcClient.client.cmsDisplayOrder.changeCmsDisplayOrder.mutate({
                        component_type: `${promoCarouselComponentType}-${this.carouselType}`,
                        data: {
                            order: [...this.componentIdsList, componentId],
                        },
                    });
                    await this.cmsDisplayOrderState.displayOrderResource.refresh();
                    await this.promoCarouselListResource.refresh();
                }
            }
        }
    };

    @action public setCarousel = async (carousel: HomePageCarouselModelType): Promise<void> => {
        if (this.isDuplicateMode === false) {
            this.isEditMode = true;
        }

        this.editedPromoId = carousel.id;

        const event_id = carousel.event_id ?? '';
        const competition_id = carousel.competition_id ?? '';
        const game_id = carousel.game_id ?? '';
        const market_id = carousel.market_id ?? '';

        this.onModifyHomePageCarousel = carousel;

        this.actionTemplate.setValue(carousel.action_template ?? '');
        this.campaignId.setValue(carousel.campaign_id ?? '');
        this.bgColor.setValue(carousel.bg_color ?? '');
        this.btnColor.setValue(carousel.btn_color ?? '');
        this.btnLink.setValue(carousel.btn_link ?? '');
        this.btnTitle.setValue(carousel.btn_title ?? '');
        this.competitionId.setValue(competition_id.toString());
        this.dateFrom.setValue(carousel.date_from);
        this.dateTo.setValue(carousel.date_to);
        this.eventId.setValue(event_id.toString());
        this.gameId.setValue(game_id.toString());
        this.imgUrl.setValue(carousel.img_url ?? '');
        this.isActive.setValue(carousel.is_active);
        this.marketId.setValue(event_id !== '' && market_id !== '' ? TypeId.newMarketId(event_id, market_id) : '');
        this.subtitle.setValue(carousel.subtitle);
        this.tcText.setValue(carousel.tc_text ?? '');
        this.title.setValue(carousel.title);
        this.sport.setValue(carousel.sport ?? '');
        this.isDesktop.setValue(carousel.is_desktop);
        this.isMobile.setValue(carousel.is_mobile);
        this.xtremepushCampaignUrl.setValue(carousel.xtremepush_campaign_url ?? '');
        this.eventModelId = event_id === '' ? null : event_id;
        this.selectedDevice =
            this.isDesktop.value && this.isMobile.value ? 'both' : this.isDesktop.value ? 'desktop' : 'mobile';
        this.hiddenLoggedInValue = carousel.visibility_mode === 'hide' ? 'hide' : 'show';
        this.promoBannedCustomerValue =
            carousel.exclude_promo_banned === false ? PromoBannedCustomerType.Include : PromoBannedCustomerType.Exclude;
        const filter = carousel.email_opt_in_filter ?? 'optedIn';
        const optedValue =
            filter === 'optedIn'
                ? EmailOptedType.OptedIn
                : filter === 'all'
                  ? EmailOptedType.All
                  : EmailOptedType.OptedOut;

        this.emailOptedValue = optedValue;
        if (isSelectionsDetails(carousel.selections_details)) {
            this.selectedSelections = carousel.selections_details.map((elem) => elem.id);
            this.selectedSelectionsInner = carousel.selections_details;
        }

        if (
            carousel.media_type === HomePageCarouselItemType.Betting ||
            carousel.media_type === HomePageCarouselItemType.Promo ||
            carousel.media_type === HomePageCarouselItemType.BonusEngine
        ) {
            this.homePageCarouselItemType = carousel.media_type;
        }

        this.uploadIcon.handleChooseImage(carousel.img_url ?? '');
        this.idsList.setValue(typeof carousel.users_ids === 'string' ? carousel.users_ids : '');

        const stakeFactorRange =
            typeof carousel.stake_factor_range === 'string' ? JSON.parse(carousel.stake_factor_range) : undefined;
        const stakeFactorRangeMin = stakeFactorRange?.min ?? stakeFactorRange?.min;
        const stakeFactorRangeMax = stakeFactorRange?.max ?? stakeFactorRange?.max;
        if (
            (typeof stakeFactorRangeMin === 'string' && stakeFactorRangeMin.trim() !== '') ||
            (typeof stakeFactorRangeMax === 'string' && stakeFactorRangeMax.trim() !== '')
        ) {
            this.stakeFactorValue = 'range';
            this.stakeFactorRangeMin.setValue(stakeFactorRangeMin ?? '');
            this.stakeFactorRangeMax.setValue(stakeFactorRangeMax ?? '');
        } else {
            this.stakeFactorValue = 'list';
            this.stakeFactor.setValue(carousel.stake_factor?.join(',') ?? '');
        }
    };

    @action public handleEdit = async (): Promise<void> => {
        for (const formLang of this.deleteFormIds) {
            await this.removeTranslationsByLang(formLang);
        }

        for (const translation of this.onEditTranslations) {
            await this.editTranslations(translation.id, translation.id);
        }

        if (this.onModifyHomePageCarousel !== null) {
            if (this.validateTranslation() === false) {
                return;
            }
            await this.addTranslations(this.onModifyHomePageCarousel.id);
        }

        this.form.setAsVisited();
        const result = this.form.result;

        if (this.isBettingSelectionValid === false) {
            await this.handleBettingError('At least one selection needed');
        }

        if (result.value.type === 'ok' && this.onModifyHomePageCarousel !== null && this.isBettingSelectionValid) {
            const payload = {
                ...result.value.data,
                img_url: this.uploadIcon.imgUrl,
                display_order: this.onModifyHomePageCarousel.display_order,
                selections_details: this.selectionsDetailsList,
                media_type: this.onModifyHomePageCarousel.media_type,
                carousel_type: this.carouselType,
                users_ids: this.idsListParsed,
                stake_factor: this.stakeFactorValue === 'list' ? this.stakeFactor.value.split(',') : null,
                stake_factor_range:
                    this.stakeFactorValue === 'range'
                        ? JSON.stringify({ min: this.stakeFactorRangeMin.value, max: this.stakeFactorRangeMax.value })
                        : null,
                email_opt_in_filter: this.emailOptedValue,
                exclude_promo_banned: this.promoBannedCustomerValue === PromoBannedCustomerType.Exclude ? true : false,
                visibility_mode: this.hiddenLoggedInValue,
            };
            const submitResult = await this.handleEditApiCall(payload, this.onModifyHomePageCarousel.id);

            if (submitResult === null) {
                console.log('Error');
            } else {
                await this.promoCarouselListResource.refresh();
                await this.handleChangeStatus(this.onModifyHomePageCarousel.id);
                this.promoCarouselListOrder = [];
                this.resetForm();
            }
        }
    };

    @action private handleEditApiCall = async (
        homePageCarousel: HomePageCarouselBodyType,
        id: number
    ): Promise<number | null | unknown> => {
        try {
            const response = await this.trpcClient.client.homePageCarousel.putUpdateHomePageCarousel.mutate({
                component_id: id,
                data: homePageCarousel,
            });
            return response.body ?? null;
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    private editTranslations = async (id: number, formId: number): Promise<void> => {
        const form = this.translationFormStateMap.get(formId);
        const formValue = form.getFormValue();
        if (formValue !== null) {
            await this.handleEditTranslation(formValue, id);
        }
    };

    @action public handleEditTranslation = async (
        formValue: PromoCarouselTranslationFormType,
        id: number
    ): Promise<void> => {
        const data = {
            title: formValue.title,
            subtitle: formValue.subtitle,
            btn_title: formValue.btn_title,
            tc_text: formValue.tc_text,
        };

        const submitResult = await this.cmsTranslationsState.handleEditApiCall(data, id);
        if (submitResult === null) {
            console.log('Error');
            await this.handleError('Something went wrong editing translation.');
        } else {
            await this.cmsTranslationsState.translationsResource.refresh();
        }
    };

    @action public setCarouselTranslations = (carouselTranslations: Array<CmsTranslationDataModelType>): void => {
        this.onEditTranslations = carouselTranslations;
        for (const translation of this.onEditTranslations) {
            const translatedData = translation.translation;
            if (isTranslation(translatedData)) {
                this.addLanguageForm(null, translation.id);
                const translationForm = this.getTranslationFormStateById(translation.id);
                if (this.isDuplicateMode) {
                    translationForm.isDuplicatedForm = true;
                } else {
                    translationForm.isDuplicatedForm = false;
                }
                translationForm.isNewForm = false;
                translationForm.title.setValue(translatedData.title);
                translationForm.subtitle.setValue(translatedData.subtitle);
                translationForm.btnTitle.setValue(translatedData.btn_title);
                translationForm.tcText.setValue(translatedData.tc_text);
                translationForm.language.setValue(translation.language);

                const translationLanguage = this.languagesState.getLanguageByValue(translation.language);
                if (translationLanguage !== null) {
                    this.allSelectedLanguages = [translationLanguage, ...this.allSelectedLanguages];
                }
            }
        }
    };

    @action public handleDeleteTranslation = async (lang: string): Promise<void> => {
        if (this.isEditMode && this.onModifyHomePageCarousel !== null) {
            this.deleteFormIds = [...this.deleteFormIds, lang];

            this.removeTranslationsForm(lang);
        } else {
            this.removeTranslationsForm(lang);
        }

        const deletedLanguage = this.allSelectedLanguages.find((item) => item.value === lang);
        if (deletedLanguage !== undefined) {
            const index = this.allSelectedLanguages.indexOf(deletedLanguage);
            this.allSelectedLanguages.splice(index, 1);
        }

        this.showDeletePopup = false;
    };

    @action private removeTranslationsByLang = async (lang: string): Promise<void> => {
        const translation = this.onEditTranslations.find((option) => option.language === lang);
        if (translation !== undefined) {
            await this.trpcClient.client.cmsTranslation.deleteCmsTranslation.mutate({
                id: translation.id,
            });
            await this.cmsTranslationsState.translationsResource.refresh();
            this.getTranslationFormStateById(translation.id).resetForm();
        }
    };

    @action private removeTranslationsForm = (lang: string): void => {
        for (const id of this.formsIds) {
            const form = this.getTranslationFormStateById(id);
            const formLang = form.language.value;
            if (lang === formLang) {
                if (this.isEditMode === false) {
                    form.resetForm();
                }
                this.formsIds = this.formsIds.filter((elem) => elem !== id);
            }
        }
    };

    public removeDisplayOrder = async (): Promise<void> => {
        await this.trpcClient.client.cmsDisplayOrder.changeCmsDisplayOrder.mutate({
            component_type: `${promoCarouselComponentType}-${this.carouselType}`,
            data: {
                order: this.componentIdsList,
            },
        });
        await this.cmsDisplayOrderState.displayOrderResource.refresh();
    };

    @action public handleListOrder = async ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
    }): Promise<void> => {
        const orderArrayMoved = arrayMove(this.componentIdsList, oldIndex, newIndex);
        this.promoCarouselListOrder = orderArrayMoved;

        await this.trpcClient.client.cmsDisplayOrder.changeCmsDisplayOrder.mutate({
            component_type: `${promoCarouselComponentType}-${this.carouselType}`,
            data: {
                order: orderArrayMoved,
            },
        });

        await this.cmsDisplayOrderState.displayOrderResource.refresh();
        await this.promoCarouselListResource.refresh();
    };

    @computed public get cmsTranslationsPromoCarouselList(): Array<CmsTranslationDataModelType> {
        return this.cmsTranslationsState.cmsTranslationsList.filter(
            (item) => item.component_type === promoCarouselComponentType
        );
    }

    @computed public get promoCarouselList(): Array<HomePageCarouselModelType> {
        if (this.promoCarouselListResponse.type === 'ready') {
            return this.promoCarouselListResponse.value;
        }
        return [];
    }

    @computed public get promoCarouselLiveScheduled(): Array<HomePageCarouselModelType> {
        const currentDate = dayjs();

        const liveCarousels = this.promoCarouselList.filter((carousel) => {
            const startDate = dayjs(carousel.date_from);
            const endDate = dayjs(carousel.date_to);
            return (
                currentDate.isSame(startDate) ||
                (currentDate.isAfter(startDate) && currentDate.isBefore(endDate)) ||
                currentDate.isBefore(startDate)
            );
        });

        return liveCarousels;
    }

    @computed public get promoCarouselExpired(): Array<HomePageCarouselModelType> {
        const currentDate = dayjs();

        const expiredCarousels = this.promoCarouselList.filter((carousel) => {
            const endDate = dayjs(carousel.date_to);
            return currentDate.isAfter(endDate);
        });

        return expiredCarousels;
    }

    @computed public get promoCarouselSorted(): Array<HomePageCarouselModelType> {
        if (this.promoCarouselListOrder.length > 0) {
            const promoCarouselsArray = [];

            for (const orderItem of this.promoCarouselListOrder) {
                const promoCarouselItem = this.promoCarouselList.find((promo) => promo.id === orderItem);
                if (promoCarouselItem !== undefined) {
                    promoCarouselsArray.push(promoCarouselItem);
                }
            }

            return promoCarouselsArray;
        }

        return this.promoCarouselLiveScheduled.sort((a, b) => b.display_order - a.display_order);
    }

    public get deviceOptions(): Array<OptionTypeOldType> {
        const devicesList = [
            {
                key: 'both',
                name: 'Both',
            },
            {
                key: 'mobile',
                name: 'Mobile',
            },
            {
                key: 'desktop',
                name: 'Desktop',
            },
        ];

        return devicesList;
    }

    public get stakeFactorSwitchOption(): Array<OptionTypeOldType> {
        const stakeFactorSwitchList = [
            {
                key: 'list',
                name: 'List of specific users stake factor',
            },
            {
                key: 'range',
                name: 'Range of stake factor',
            },
        ];

        return stakeFactorSwitchList;
    }

    public get emailOpteSwitchdOptions(): Array<OptionTypeOldType> {
        const emailOptedList = [
            {
                key: 'optedIn',
                name: 'Opted In',
            },
            {
                key: 'optedOut',
                name: 'Opted Out',
            },
            {
                key: 'all',
                name: 'All',
            },
        ];

        return emailOptedList;
    }

    public get hiddenLoggedInOptions(): Array<OptionTypeOldType> {
        const hiddenLoggedInList = [
            {
                key: 'hide',
                name: 'Hide',
            },
            {
                key: 'show',
                name: 'Show',
            },
        ];

        return hiddenLoggedInList;
    }

    public get promoBannedCustomerOptions(): Array<OptionTypeOldType> {
        const promoBannedCustomerList = [
            {
                key: 'exclude',
                name: 'Exclude',
            },
            {
                key: 'include',
                name: 'Include',
            },
        ];

        return promoBannedCustomerList;
    }

    public onChangeHiddenLoggedInOptions = (value: string): void => {
        if (value === 'hide') {
            this.hiddenLoggedInValue = 'hide';
        } else {
            this.hiddenLoggedInValue = 'show';
        }
    };

    public onChangeEmailOptedOptions = (value: string): void => {
        if (value === 'optedIn') {
            this.emailOptedValue = EmailOptedType.OptedIn;
        } else if (value === 'optedOut') {
            this.emailOptedValue = EmailOptedType.OptedOut;
        } else {
            this.emailOptedValue = EmailOptedType.All;
        }
    };

    public onChangePromoBannedCustomerOptions = (value: string): void => {
        if (value === 'exclude') {
            this.promoBannedCustomerValue = PromoBannedCustomerType.Exclude;
        } else {
            this.promoBannedCustomerValue = PromoBannedCustomerType.Include;
        }
    };

    public get redirectOptions(): Array<OptionTypeOldType> {
        const redirectionList = [
            {
                key: 'direct-link',
                name: 'Direct link',
            },
        ];

        if (this.isHomePage) {
            redirectionList.push(
                {
                    key: 'event-id',
                    name: 'Event id',
                },
                {
                    key: 'sport',
                    name: 'Sport',
                },
                {
                    key: 'competition-id',
                    name: 'Competition id',
                },
                {
                    key: 'casino-game-id',
                    name: 'Casino game',
                }
            );
        } else {
            redirectionList.push({
                key: 'casino-game-id',
                name: 'Casino game',
            });
        }
        return redirectionList;
    }

    @computed private get sportList(): SportsListType {
        const allSports = this.allSports.get();
        if (allSports.type === 'ready') {
            return allSports.value;
        }
        return [];
    }

    @computed private get campaignsListData(): {
        status: 'loading' | 'error' | 'ready';
        list: Array<CampaignModel>;
    } {
        const result = this.campaignsResourse.get();
        const status = result.type;

        return {
            status,
            list:
                status === 'ready'
                    ? result.value.filter((data) => data.status === 'active').map((data) => new CampaignModel(data))
                    : [],
        };
    }

    @computed public get campaignsListOptions(): Array<OptionTypeOldType> {
        return this.campaignsListData.list.map((elem) => {
            const { id, campaignName } = elem;
            return {
                key: id,
                name: campaignName,
            };
        });
    }

    @computed public get campaignsListLoading(): boolean {
        return this.campaignsListData.status === 'loading';
    }

    @computed public getPromoLabel(source: HomePageCarouselItemType | string): PromoLabel | string {
        switch (source) {
            case HomePageCarouselItemType.Promo:
                return PromoLabel.Promo;
            case HomePageCarouselItemType.Betting:
                return PromoLabel.Betting;
            case HomePageCarouselItemType.BonusEngine:
                return PromoLabel.BonusEngine;
            default:
                return '';
        }
    }

    public get tabs(): HomePageCarouselTabType[] {
        const getFullLabel = (promoLabel: PromoLabel): string => `${this.isEditMode ? 'Edit' : 'Create'} ${promoLabel}`;
        const tabs = [
            {
                dataTest: 'default-promo',
                type: HomePageCarouselItemType.Promo,
                label: getFullLabel(PromoLabel.Promo),
            },
        ];

        if (this.isHomePage) {
            tabs.push({
                dataTest: 'betting-promo',
                type: HomePageCarouselItemType.Betting,
                label: getFullLabel(PromoLabel.Betting),
            });
        }

        tabs.push({
            dataTest: 'bonus-engine-promo',
            type: HomePageCarouselItemType.BonusEngine,
            label: getFullLabel(PromoLabel.BonusEngine),
        });

        return tabs;
    }

    @computed public get sportListOptions(): Array<OptionTypeOldType> {
        const temp = this.sportList.map((elem) => {
            return {
                key: elem.id,
                name: elem.name,
            };
        });

        return temp;
    }

    // LIST

    @computed private get promoCarouselListResponse(): HomePageCarouselContentResponseType {
        const homePageCarousel = this.promoCarouselListResource.get();
        if (homePageCarousel.type === 'ready') {
            return {
                type: 'ready',
                value: homePageCarousel.value,
            };
        }
        if (homePageCarousel.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching home page carousel',
        };
    }

    @action public setDesktopFlag = async (homePageCarousel: HomePageCarouselModelType): Promise<void> => {
        const payload: HomePageCarouselModelType = {
            ...homePageCarousel,
            is_desktop: !homePageCarousel.is_desktop,
        };

        await this.handleEditActiveFlags(payload);
    };

    @action public setMobileFlag = async (homePageCarousel: HomePageCarouselModelType): Promise<void> => {
        const payload: HomePageCarouselModelType = {
            ...homePageCarousel,
            is_mobile: !homePageCarousel.is_mobile,
        };

        await this.handleEditActiveFlags(payload);
    };

    @action private handleEditActiveFlags = async (payload: HomePageCarouselModelType): Promise<void> => {
        const submitResult = await this.handleEditApiCall(payload, payload.id);

        if (submitResult === null) {
            console.log('Error');
        } else {
            await this.promoCarouselListResource.refresh();
            this.resetForm();
        }
    };

    @action private handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await this.wait(3000);
        this.errorMessageForList = null;
    };

    @action private handleBettingError = async (message: string): Promise<void> => {
        this.errorMessageBetting = message;
        await this.wait(3000);
        this.errorMessageBetting = null;
    };

    private wait = async (time: number): Promise<void> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, time);
        });
    };

    @action public onChangeActionTemplate = (): void => {
        this.competitionId.reset();
        this.eventId.reset();
        this.gameId.reset();
        this.sport.reset();
        this.btnLink.reset();
    };

    @action public onCampaignSelect = (id: string): void => {
        const isString = (value: unknown): value is string => typeof value === 'string';
        const selectedCampaign = this.campaignsListData.list.find((campaign) => campaign.id === id) ?? null;
        const campaignName = selectedCampaign?.campaignName;
        const description = selectedCampaign?.description;
        const data = selectedCampaign?.data;
        const startAt = apiDateToFormDate(data?.startAt);
        const expiryAt = apiDateToFormDate(data?.expiryAt);

        this.title.setValue(isString(campaignName) ? campaignName : '');
        this.tcText.setValue(isString(description) ? description : '');
        this.dateFrom.setValue(isString(startAt) ? startAt : '');
        this.dateTo.setValue(isString(expiryAt) ? expiryAt : '');
        this.campaignId.setValue(isString(id) ? id : '');
    };

    public handleChangeWithConfirmation = (dateType: 'start' | 'end', onConfirm: (date: Date | null) => void) => {
        return (date: Date | null): void => {
            const popupsStack = PopupsStackState.get(this.common);
            popupsStack.addPopup('change_date', {
                template: 'generic',
                title: `Change ${dateType[0]?.toUpperCase() + dateType.slice(1)} Date`,
                message: (
                    <p className='m-0'>
                        {`By changing the ${dateType} date your promo carousel card won’t be in sync with the bonus engine
                        campaign ${dateType} date. Please confirm that you would like to proceed with this action`}
                    </p>
                ),
                hasOverlay: true,
                cancel_label: 'Cancel',
                confirm_label: 'Continue',
                onConfirm: () => {
                    onConfirm(date);
                    popupsStack.closePopupOnTop();
                },
                onCancel: () => {
                    popupsStack.closePopupOnTop();
                },
            });
        };
    };

    @action public onChangeDevice = (value: string): void => {
        if (value === 'mobile') {
            this.selectedDevice = 'mobile';
            this.isMobile.setValue(true);
            this.isDesktop.setValue(false);
        }
        if (value === 'desktop') {
            this.selectedDevice = 'desktop';
            this.isMobile.setValue(false);
            this.isDesktop.setValue(true);
        }
        if (value === 'both') {
            this.selectedDevice = 'both';
            this.isMobile.setValue(true);
            this.isDesktop.setValue(true);
        }
    };

    @action public onChangeStakeFactorOptions = (value: string): void => {
        if (value === 'list') {
            this.stakeFactorValue = 'list';
            this.stakeFactorRangeMax.setValue('');
            this.stakeFactorRangeMin.setValue('');
        }
        if (value === 'range') {
            this.stakeFactorValue = 'range';
            this.stakeFactor.setValue('');
        }
    };

    // VALIDATORS

    private validateTranslation = (): boolean => {
        let isValid = true;
        for (const formId of this.formsIds) {
            const isNewForm = this.getTranslationFormStateById(formId).isNewForm;
            const isDuplicatedForm = this.getTranslationFormStateById(formId).isDuplicatedForm;
            if (isNewForm === true || isDuplicatedForm === true) {
                const form = this.translationFormStateMap.get(formId);
                const formValue = form.getFormValue();
                if (formValue === null) {
                    isValid = false;
                }
            }
        }

        return isValid;
    };

    @action private canCampaignBeEmpty =
        () =>
        (value: string): Result<string | string> => {
            if (value === '') {
                return Result.createError('You must choose a campaign');
            }
            return Result.createOk(value);
        };

    @action private canGameBeNull =
        () =>
        (value: string): Result<number | null> => {
            if (this.actionTemplate.value === 'casino-game-id') {
                if (value === '') {
                    return Result.createError('You must pick a game');
                }
                const valueNumber = parseFloat(value);
                return isNaN(valueNumber)
                    ? Result.createError('Game ID must be a number')
                    : Result.createOk(valueNumber);
            }

            return Result.createOk(null);
        };

    @action private canCompetitionBeNull =
        () =>
        (value: string): Result<number | null> => {
            if (this.actionTemplate.value === 'competition-id') {
                if (value === '') {
                    return Result.createError('You must provide a Competition ID');
                }
                const valueNumber = parseFloat(value);
                return isNaN(valueNumber)
                    ? Result.createError('Competition ID must be a number')
                    : Result.createOk(valueNumber);
            }

            return Result.createOk(null);
        };

    @action private canMarketBeNull =
        () =>
        (value: string): Result<number | null> => {
            if (this.homePageCarouselItemType === 'betting') {
                if (value === '') {
                    return Result.createError('You must select a market');
                }

                //@ts-expect-error
                const valueNumber = TypeId.getMarketId(value);
                return isNaN(valueNumber)
                    ? Result.createError('Market ID must be a number')
                    : Result.createOk(valueNumber);
            }

            return Result.createOk(null);
        };

    @action private canDirectLinkBeEmpty =
        () =>
        (value: string): Result<string | string> => {
            if (this.actionTemplate.value === 'direct-link') {
                if (value === '') {
                    return Result.createError('You must provide a link');
                }
                return Result.createOk(value);
            }

            return Result.createOk(value);
        };

    @action private canSportBeEmpty =
        () =>
        (value: string): Result<string | string> => {
            if (this.actionTemplate.value === 'sport' || this.actionTemplate.value === 'competition-id') {
                if (value === '') {
                    return Result.createError('You must choose a sport');
                }
                return Result.createOk(value);
            }
            return Result.createOk(value);
        };

    // EVENT

    @action private canEventBeNull =
        () =>
        (value: string): Result<number | null> => {
            if (this.actionTemplate.value === 'event-id' || this.homePageCarouselItemType === 'betting') {
                if (value === '') {
                    return Result.createError('You must provide an Event ID');
                }

                const valueNumber = parseFloat(value);
                return isNaN(valueNumber)
                    ? Result.createError('Event ID must be a number')
                    : Result.createOk(valueNumber);
            }

            return Result.createOk(null);
        };

    @action private canStakeFactorBeEmpty =
        () =>
        (value: string): Result<string | null> => {
            if (value.trim() === '') {
                return Result.createOk(null);
            }

            return convertAndValidToFloat('Stake factor must be a number')(value);
        };

    @action private canStakeFactorListBeEmpty =
        () =>
        (value: string): Result<string | null> => {
            if (value.trim() === '') {
                return Result.createOk(null);
            }

            return validateCommaSeparatedFloats('Stake factor must be a list of numbers')(value);
        };

    @action private validateEventDoesntExist =
        (message: string) =>
        (value: number | null): Result<number | null> => {
            if (this.actionTemplate.value === 'event-id' || this.homePageCarouselItemType === 'betting') {
                const eventModel = this.modelsState.getEvent(parseInt(this.eventId.value, 10));
                if (eventModel === null) {
                    return Result.createError(message);
                }
            }
            return Result.createOk(value);
        };

    @action private validateXtremepushCampaignUrl =
        () =>
        (value: string): Result<string | null> => {
            if (value === '') {
                return Result.createOk(null);
            }
            if (!value.startsWith('https://')) {
                return Result.createError('Invalid url');
            }
            if (!value.endsWith('?token=jwtToken')) {
                return Result.createError("Please include the params '?token=jwtToken' at the end of the url");
            }

            return validateLength(256, 'Max length is 256 characters')(value);
        };

    @computed public get isEventValid(): boolean {
        const eventId = this.eventId.result;
        return eventId.value.type === 'error' && this.eventId.isVisited() ? false : true;
    }

    @action public handleCheckEvent = (): void => {
        const eventId = this.eventId.result;
        this.eventModelId = null;

        if (eventId.value.type === 'ok' && eventId.value.data !== null) {
            this.eventModelId = eventId.value.data;
        }

        this.clearSelections();
        this.marketId.setValue('');
    };

    @computed public get eventModel(): EventModel | null {
        const eventId = this.eventId.result;

        if (eventId.value.type === 'ok' && eventId.value.data !== null) {
            if (this.eventModelId !== null && this.eventModelId === eventId.value.data) {
                return this.modelsState.getEvent(this.eventModelId);
            }
        }

        return null;
    }

    @computed public get eventName(): string {
        return this.eventModel?.name ?? '';
    }

    // MARKETS AND SELECTIONS

    @computed private get isBettingSelectionValid(): boolean {
        if (this.homePageCarouselItemType === 'betting') {
            const selectionsQuantity = this.selectionsDetailsList.length;

            if (selectionsQuantity > 0 && selectionsQuantity < 4) {
                return true;
            }

            return false;
        }

        return true;
    }

    public get marketsOptions(): Array<OptionTypeOldType> {
        const markets = this.eventModel?.markets ?? [];

        const marketsOptions = markets.map((elem) => {
            return {
                key: elem.id.toString(),
                name: `${elem.name} - ${elem.id}`,
            };
        });
        marketsOptions.unshift({
            key: '',
            name: 'Select market',
        });

        return marketsOptions;
    }

    @computed public get selectedMarketId(): number | null {
        const marketId = this.marketId;

        if (marketId.value !== '') {
            return parseInt(marketId.value, 10);
        }

        return null;
    }

    @action private getMarketModel = (): TypeId.MarketId | null => {
        const resultEventId = this.eventId.result;

        if (resultEventId.value.type === 'error') {
            console.error('HomePageCarouselState - getSelectionModel eventId error');
            return null;
        }

        const resultMarketId = this.marketId.result;

        if (resultMarketId.value.type === 'error') {
            console.error('HomePageCarouselState - getSelectionModel marketId error');
            return null;
        }

        const eventId = resultEventId.value.data;
        const marketId = resultMarketId.value.data;

        if (marketId !== null && eventId !== null) {
            const marketIdInner = TypeId.newMarketId(eventId, marketId);
            return marketIdInner;
        }

        return null;
    };

    @action public handlePickSelection = async (selectionInnerId: string): Promise<void> => {
        const selectionId = parseInt(selectionInnerId, 10);
        if (this.selectedSelections.length >= 3) {
            await this.handleBettingError('Cannot add more than 3 selections.');
        } else {
            const tempSet = new Set(this.selectedSelections);
            tempSet.add(selectionId);
            this.selectedSelections = Array.from(tempSet);
        }
    };

    @action public handleRemoveSelection = (selectionId: number): void => {
        this.selectedSelections = this.selectedSelections.filter((elem) => elem !== selectionId);
        this.homePageCarouselSelectionStateMap.delete(selectionId);
    };

    @computed public get selectionsOptions(): Array<OptionTypeOldType> {
        const resultEventId = this.eventId.result;

        if (resultEventId.value.type === 'error') {
            console.error('HomePageCarouselState - selectionsOptions eventId error');
            return [];
        }
        const resultMarketId = this.marketId.result;

        if (resultMarketId.value.type === 'error') {
            console.error('HomePageCarouselState - selectionsOptions marketId error');
            return [];
        }

        const eventId = resultEventId.value.data;
        const marketId = resultMarketId.value.data;

        if (marketId !== null && eventId !== null) {
            const marketIdInner = TypeId.newMarketId(eventId, marketId);

            const marketModel = this.modelsState.getMarket(marketIdInner);

            const selections = marketModel?.selections ?? [];

            return selections
                .map((elem) => {
                    return {
                        key: elem.id.toString(),
                        name: `${elem.name} - ${elem.id}`,
                    };
                })
                .filter((elem) => this.selectedSelections.includes(parseInt(elem.key, 10)) === false);
        }

        return [];
    }

    public getSelectionItemById = (selectionId: number): HomePageCarouselSelectionState => {
        return this.homePageCarouselSelectionStateMap.get(selectionId);
    };

    @action private getSelectedSelectionsLabel = (selectionId: number): string | null => {
        const selectedSelection = this.selectedSelectionsInner.find((elem) => elem.id === selectionId);
        return selectedSelection?.label ?? null;
    };

    @computed public get selectionsDetailsList(): Array<SelectionsDetailsType> {
        const selectionsDetailsList = [];
        for (const selectionId of this.selectedSelections) {
            const selectionModel = this.homePageCarouselSelectionStateMap.get(selectionId);

            if (selectionModel.id !== null) {
                selectionsDetailsList.push({
                    id: selectionModel.id,
                    label: selectionModel.value,
                });
            }
        }

        return selectionsDetailsList;
    }

    @action public handleFiltersOrder = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
        this.selectedSelections = arrayMove(this.selectedSelections, oldIndex, newIndex);
    };

    public clearSelections = (): void => {
        for (const selectionId of this.selectedSelections) {
            this.homePageCarouselSelectionStateMap.delete(selectionId);
        }
        this.selectedSelections = [];
    };

    // DATE

    @computed private get parsedStartDate(): number | null {
        return Date.parse(this.dateFrom.value);
    }

    @computed private get parsedEndDate(): number | null {
        return Date.parse(this.dateTo.value);
    }

    @computed public get startDate(): Date | null {
        return this.parsedStartDate === null || isNaN(this.parsedStartDate) ? null : new Date(this.parsedStartDate);
    }

    @computed public get endDate(): Date | null {
        return this.parsedEndDate === null || isNaN(this.parsedEndDate) ? null : new Date(this.parsedEndDate);
    }

    @action public handleBulkDelete = async (ids: number[]): Promise<void> => {
        try {
            const response = await this.trpcClient.client.homePageCarousel.deleteManyHomePageCarousels.mutate({
                ids,
                carouselType: this.carouselType,
            });

            if (response.status !== 200) {
                console.error(response.body);
                ToasterState.get(this.common).setToast({
                    variant: 'error',
                    title: 'Error',
                    message: typeof response.body === 'string' ? response.body : 'Something went wrong',
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    @action public handleShowBulkDeleteDialog = (): void => {
        this.showBulkDeleteDialog = true;
    };

    @action public handleHideBulkDeleteDialog = (): void => {
        this.showBulkDeleteDialog = false;
        this.selectedItemsForBulkDelete = [];
    };

    @action public handleToggleItemSelection = (id: number): void => {
        const index = this.selectedItemsForBulkDelete.indexOf(id);
        if (index > -1) {
            this.selectedItemsForBulkDelete = this.selectedItemsForBulkDelete.filter((itemId) => itemId !== id);
        } else {
            this.selectedItemsForBulkDelete = [...this.selectedItemsForBulkDelete, id];
        }
    };

    @action public handleToggleAllItems = (checked: boolean): void => {
        if (checked) {
            this.selectedItemsForBulkDelete = this.promoCarouselList.map((item) => item.id);
        } else {
            this.selectedItemsForBulkDelete = [];
        }
    };

    @action public handleConfirmBulkDelete = async (): Promise<void> => {
        if (this.selectedItemsForBulkDelete.length === 0) {
            return;
        }
        await this.handleBulkDelete(this.selectedItemsForBulkDelete);
        await this.promoCarouselListResource.refresh();
        this.handleHideBulkDeleteDialog();
    };
}
